import { createAsyncThunk } from "@reduxjs/toolkit";
import { ColumnFiltersState } from "@tanstack/react-table";
import { Axios } from "api/axios";
import { InventoryAxios } from "api/InventoryAxios";
import { api } from "helpers/api_url";


export interface PaginatedResponse<T> {
  count: number;
  total_pages: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

export const fetchRecordLevelWarehouseWise = createAsyncThunk(
  "inventory/fetchRecordLevelWarehouseWise",
  async ({
    page,
    pageSize,
    sort,
    order,
    search,
    filters,
    warehouseIdSearch
  }: {
    page: number;
    pageSize: number;
    search?: string;
    sort?: string;
    order?: "asc" | "desc" | "";
    filters?: any;
    warehouseIdSearch:any;
  }) => {
    try {
      const params = new URLSearchParams();
      params.append("page", page.toString());
      params.append("page_size", pageSize.toString());
      if (search) params.append("search", search);
      if (sort) {
        const orderingValue = order === "desc" ? `-${sort}` : sort;
        params.append("ordering", orderingValue);
      }

      if (filters && filters.length > 0) {
        filters.forEach(
          ({
            filterField,
            filterValue,
          }: {
            filterField: string;
            filterValue: any;
          }) => {
            if (
              filterField &&
              filterValue !== undefined &&
              filterValue !== null
            ) {
              if (typeof filterValue === "object" && filterValue !== null) {
                const { from_date, to_date, ...otherValues } = filterValue;

                // Handle date range filters for specific fields
                if (filterField === "created_at" || filterField === "updated_at") {
                  if (from_date) {
                    params.append(`${filterField}_from`, from_date);
                  }
                  if (to_date) {
                    params.append(`${filterField}_to`, to_date);
                  }
                } else {
                  // For non-date fields, include from_date and to_date if they exist
                  if (from_date !== undefined && from_date !== null && from_date !== "") {
                    params.append("from_date", String(from_date));
                  }
                  if (to_date !== undefined && to_date !== null && to_date !== "") {
                    params.append("to_date", String(to_date));
                  }
                }

                // Handle other object filters (excluding from_date and to_date which are already processed)
                Object.entries(otherValues).forEach(([key, value]) => {
                  if (value !== undefined && value !== null && value !== "") {
                    params.append(key, String(value));
                  }
                });
              } else {
                params.append(filterField, filterValue);
              }
            }
          }
        );
      }

      const response: any = await Axios.get(`${api?.Inventory?.Reorderlevel?.WarehouseWise?.listing}?warehouse_id=${warehouseIdSearch}&${params.toString()}`);

      return {
        data: response?.data?.data || [],
        rowCount: response?.data?.count || 0,
      };
    } catch (error: any) {
      console.error(
        "fetch Inventory List error:",
        error.response?.data || error.message
      );
      throw new Error("Network Error");
    }
  }
);

export const exportRecordLevelWarehouseWise = async ({
  page,
  pageSize,
  sort,
  order,
  search,
  filters,
  warehouse_id,
}: {
  page?: number;
  pageSize?: number;
  sort?: string;
  order?: "asc" | "desc" | "";
  search?: string;
  filters?: any;
  warehouse_id?: string;
}) => {
  const params = new URLSearchParams();

  params.append("export", "csv");

  if (warehouse_id) {
    params.append("warehouse_id", warehouse_id);
  }

  if (search) params.append("search", search);

  // Handle sorting
  if (sort) {
    const orderingVal = order === "desc" ? `-${sort}` : sort;
    params.append("ordering", orderingVal);
  }

  // ✅ handle dynamic filters
  filters?.forEach(({ filterField, filterValue }: any) => {
    if (filterValue === undefined || filterValue === null || filterValue === "") return;

    // Handle date range filters (object with from_date / to_date)
    if (typeof filterValue === "object" && filterValue !== null) {
      const { from_date, to_date, ...otherValues } = filterValue;

      // Handle date range filters for specific fields
      if (filterField === "inward_date" || filterField === "created_at" || filterField === "updated_at") {
        if (from_date) {
          params.append(`${filterField}_from`, from_date);
        }
        if (to_date) {
          params.append(`${filterField}_to`, to_date);
        }
      } else {
        // For non-date fields, include from_date and to_date if they exist
        if (from_date !== undefined && from_date !== null && from_date !== "") {
          params.append("from_date", String(from_date));
        }
        if (to_date !== undefined && to_date !== null && to_date !== "") {
          params.append("to_date", String(to_date));
        }
      }

      // Handle other object filters (excluding from_date and to_date which are already processed)
      Object.entries(otherValues).forEach(([key, val]) => {
        if (val !== undefined && val !== null && val !== "") {
          params.append(key, String(val));
        }
      });
    } else {
      // Handle simple string/number filters
      params.append(filterField, String(filterValue));
    }
  });

  const res = await Axios.get(
    `${api?.Inventory?.Reorderlevel?.WarehouseWise?.listing}?${params.toString()}`,
    { responseType: "blob" }
  );

  return {
    blob: res.data,
    filename:
      res.headers["content-disposition"]
        ?.split("filename=")[1]
        ?.replace(/"/g, "") || "RecordLevelWarehouseWise.csv",
  };
};
